<template>
  <ion-app>
    <AppMenu v-if="isDesktop" />
    <ion-content id="main-content">
      <div v-if="!loading">
        <ion-router-outlet />
        <ReportModal />
        <RegistrationModal v-if="uiStore.showCompanyInput" />
      </div>
    </ion-content>
  </ion-app>
</template>

<script setup lang="ts">
import { App } from '@capacitor/app';
import {
  IonApp,
  IonRouterOutlet,
  IonContent,
  useIonRouter,
  isPlatform,
  useKeyboard,
  useBackButton
} from '@ionic/vue';
import { useUserStore } from '@/store/user';
import ReportModal from '@/components/incident-modal/IncidentModal.vue';
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useLocalStorage from '@/composeables/useLocalStorage';
import { PushNotifications } from '@capacitor/push-notifications';
import { useUiStore } from '@/store/uiStore';
import { SplashScreen } from '@capacitor/splash-screen';
import useApi from '@/composeables/useApi';
import { simpleAlert } from '@/utils/alert';
import { useFilterStore } from '@/store/filters';
import IncidentStatus from '@/constants/incident-status';
import { useIncidentStore } from '@/store/incident';
import { Capacitor } from '@capacitor/core';
import { DEFAULT_SYSTEM_BAR_STYLE, setSystemBarStyle } from '@/composeables/useSystemBarStyle';
import AppMenu from '@/components/AppMenu.vue';
import RegistrationModal from '@/components/RegistrationModal.vue';
import {useAzureAuth, getIntendedDestination, clearIntendedDestination} from "@/composeables/useAzureAuth";
import { useRoute } from 'vue-router';

const uiStore = useUiStore();
const userStore = useUserStore();
const ionRouter = useIonRouter();
const route = useRoute();
const { apiPut } = useApi();
const filterStore = useFilterStore();
const incidentStore = useIncidentStore();
const { getTokenSilently } = useAzureAuth();

const { isDesktop } = storeToRefs(uiStore);
const { fetchedUserCompleted, currentUser } = storeToRefs(userStore);
const { incident, incidentModalOpen } = storeToRefs(incidentStore);

const loading = ref(true);
App.addListener('appStateChange', state => {
  if (state.isActive) {
    uiStore.setAppActive(true);
    uiStore.setBadgeCount();
    uiStore.clearAllNotifications();
  }
});

onMounted(async() => {
  // Views with a light header opt out via useLightSystemBars().
  await setSystemBarStyle(DEFAULT_SYSTEM_BAR_STYLE);
  uiStore.updateWidth();
  window.addEventListener('resize', uiStore.updateWidth);
  await SplashScreen.hide();
  await getTokenSilently();
  await addListeners();
  await registerNotifications();
  await userStore.fetchUser();
  await filterStore.fetchFilters();
  await uiStore.setBadgeCount();

  if (fetchedUserCompleted.value) {
    await handleAuthentication();
  }
});

watch(
  fetchedUserCompleted,
  () => {
    if (fetchedUserCompleted.value) {
      handleAuthentication();
    }
  }
);

userStore.$onAction(({
  name,
  store,
  after,
  onError,
}) => {
  after(async() => {
    if (name === 'signIn' && store.currentUser) {
      await registerNotifications();
      return;
    }
  });

  onError((error) => {
    console.warn(`Failed "${name}"\nError: ${error}.`);
  });
});

const registerNotifications = async() => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  let permStatus = await PushNotifications.checkPermissions();

  if (permStatus.receive === 'prompt') {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive !== 'granted') {
    return;
  }

  await PushNotifications.register();
};

const addListeners = async() => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await PushNotifications.addListener('registration', token => {
    const deviceType = isPlatform('ios') ? 0 : 1;
    apiPut(`/users/${currentUser.value.id}/device`, {
      device_token: token.value,
      type: deviceType,
    });
  });

  await PushNotifications.addListener('registrationError', err => {
    simpleAlert('Något gick fel', err.error);
  });

  await PushNotifications.addListener('pushNotificationReceived', () => {
    uiStore.setBadgeCount();
  });
};

const isPublicLoggedOutWebRoute = () => {
  if (Capacitor.isNativePlatform()) {
    return false;
  }

  if (route.name === 'singleSecurityWarning') {
    return true;
  }

  return route.name === 'warnings' && route.query.tab === 'securityCenter';
};

/**
 * In .env, add VITE_ALLOW_DEV_NAVIGATION=1 to disable this
 * this will make it possible to navigate by url in dev
 * @todo - should maybe be deleted before go live
 */
const handleAuthentication = async() => {
  const localStorage = await useLocalStorage();
  if (import.meta.env.VITE_ALLOW_DEV_NAVIGATION === '1') {
    loading.value = false;
    return;
  }

  const loggedOutUsage = await localStorage.get('loggedOutUsage');

  if (!currentUser.value && !loggedOutUsage && !isPublicLoggedOutWebRoute()) {
    ionRouter.replace('/auth');
  } else if (currentUser.value) {
    // For returning users already logged in via SSO, redirect to saved destination
    const savedDestination = getIntendedDestination();
    if (savedDestination) {
      const currentPath = window.location.pathname + window.location.search;
      clearIntendedDestination();
      if (savedDestination !== currentPath) {
        ionRouter.replace(savedDestination);
      }
    }
  }
  loading.value = false;
};

useBackButton(101, async(processNextHandler) => {
  if (incidentModalOpen.value) {
    if (incident.value && incident.value.status !== IncidentStatus.Completed) {
      await incidentStore.leaveIncidentReport();
    } else {
      incidentStore.closeIncidentModal();
    }
  } else {
    processNextHandler();
  }
});

</script>
